import {
  adminSidebarItems,
  sidebarItems,
  staffSidebarItems,
} from "@/app/ui/dashboard/sidebarConfig";
import i18n from "@/i18n";
import { getInitialLanguage } from "@/lib/language";
import { create } from "zustand";
import { persist } from "zustand/middleware";

type SidebarContext = "user" | "staff" | "admin";

interface AppState {
  language: string;
  languageManuallySelected: boolean;
  setLanguage: (lang: string) => void;
  applyLanguageFallback: (lang: string) => void;
  selectedTeam: string | null;
  setSelectedTeam: (team: string) => void;

  // Add context to store
  currentContext: SidebarContext;
  setCurrentContext: (context: SidebarContext) => void;

  // Context-specific routes
  pinnedRoutes: Record<SidebarContext, string[]>;
  sectionRoutes: Record<SidebarContext, string[]>;

  // Context-aware methods
  pinRoute: (route: string) => void;
  unpinRoute: (route: string) => void;
  resetPinnedRoutes: () => void;
  expandSection: (route: string) => void;
  collapseSection: (route: string) => void;
  resetSectionRoutes: () => void;
  toggleAllSectionRoutes: () => void;
  toggleGroup: (groupValue: string) => void;
  openAllSectionRoutes: () => void;
  closeAllSectionRoutes: () => void;

  // Getters for current context
  getCurrentPinnedRoutes: () => string[];
  getCurrentSectionRoutes: () => string[];
  initializeRoutesForContext: (context: SidebarContext) => void;

  // Add sidebar state awareness
  handleSidebarCollapse: (isCollapsed: boolean) => void;
}

// Helper to get sidebar items dynamically
const getSidebarItemsByContext = (context: SidebarContext) => {
  switch (context) {
    case "staff":
      return staffSidebarItems;
    case "admin":
      return adminSidebarItems;
    default:
      return sidebarItems;
  }
};

// Helper to get initial pinned routes for a context
const getInitialPinnedRoutes = (context: SidebarContext): string[] => {
  const items = getSidebarItemsByContext(context);
  return items.flatMap((group) => group.links.map((link) => link.value));
};

// Helper to get initial section routes for a context
const getInitialSectionRoutes = (context: SidebarContext): string[] => {
  const items = getSidebarItemsByContext(context);
  return items.map((group) => group.value);
};

export const useAppStore = create<AppState>()(
  persist(
    (set, get) => ({
      language: getInitialLanguage(),
      languageManuallySelected: false,
      setLanguage: (lang) => {
        i18n.changeLanguage(lang);
        set({ language: lang, languageManuallySelected: true });
        if (typeof window !== "undefined") {
          localStorage.setItem(
            "app-storage",
            JSON.stringify({ state: { language: lang, languageManuallySelected: true } })
          );
        }
      },
      applyLanguageFallback: (lang) => {
        i18n.changeLanguage(lang);
        set({ language: lang, languageManuallySelected: false });
      },

      selectedTeam: null,
      setSelectedTeam: (team) => set({ selectedTeam: team }),

      currentContext: "user",
      setCurrentContext: (context) => {
        set((state) => {
          // Initialize routes for new context if they don't exist
          const newPinnedRoutes = { ...state.pinnedRoutes };
          const newSectionRoutes = { ...state.sectionRoutes };

          if (
            !newPinnedRoutes[context] ||
            newPinnedRoutes[context].length === 0
          ) {
            newPinnedRoutes[context] = getInitialPinnedRoutes(context);
          }

          if (
            !newSectionRoutes[context] ||
            newSectionRoutes[context].length === 0
          ) {
            newSectionRoutes[context] = getInitialSectionRoutes(context);
          }

          return {
            currentContext: context,
            pinnedRoutes: newPinnedRoutes,
            sectionRoutes: newSectionRoutes,
          };
        });
      },

      // Initialize with default routes for user context
      pinnedRoutes: {
        user: getInitialPinnedRoutes("user"),
        staff: [],
        admin: [],
      },
      sectionRoutes: {
        user: getInitialSectionRoutes("user"),
        staff: [],
        admin: [],
      },

      // Context-aware methods
      pinRoute: (route) =>
        set((state) => {
          const context = state.currentContext;
          const currentPinned = state.pinnedRoutes[context] || [];

          return {
            pinnedRoutes: {
              ...state.pinnedRoutes,
              [context]: [...new Set([...currentPinned, route])],
            },
          };
        }),

      unpinRoute: (route) =>
        set((state) => {
          const context = state.currentContext;
          const currentPinned = state.pinnedRoutes[context] || [];

          return {
            pinnedRoutes: {
              ...state.pinnedRoutes,
              [context]: currentPinned.filter((r) => r !== route),
            },
          };
        }),

      resetPinnedRoutes: () =>
        set((state) => {
          const context = state.currentContext;
          return {
            pinnedRoutes: {
              ...state.pinnedRoutes,
              [context]: getInitialPinnedRoutes(context),
            },
          };
        }),

      expandSection: (route) =>
        set((state) => {
          const context = state.currentContext;
          const currentSections = state.sectionRoutes[context] || [];

          return {
            sectionRoutes: {
              ...state.sectionRoutes,
              [context]: [...new Set([...currentSections, route])],
            },
          };
        }),

      collapseSection: (route) =>
        set((state) => {
          const context = state.currentContext;
          const currentSections = state.sectionRoutes[context] || [];

          return {
            sectionRoutes: {
              ...state.sectionRoutes,
              [context]: currentSections.filter((r) => r !== route),
            },
          };
        }),

      resetSectionRoutes: () =>
        set((state) => {
          const context = state.currentContext;
          return {
            sectionRoutes: {
              ...state.sectionRoutes,
              [context]: getInitialSectionRoutes(context),
            },
          };
        }),

      toggleAllSectionRoutes: () =>
        set((state) => {
          const context = state.currentContext;
          const items = getSidebarItemsByContext(context);
          const currentRoutes = state.sectionRoutes[context] || [];

          return {
            sectionRoutes: {
              ...state.sectionRoutes,
              [context]:
                currentRoutes.length === items.length
                  ? []
                  : items.map((group) => group.value),
            },
          };
        }),

      toggleGroup: (groupValue) =>
        set((state) => {
          const context = state.currentContext;
          const currentRoutes = state.sectionRoutes[context] || [];

          return {
            sectionRoutes: {
              ...state.sectionRoutes,
              [context]: currentRoutes.includes(groupValue)
                ? currentRoutes.filter((item) => item !== groupValue)
                : [...currentRoutes, groupValue],
            },
          };
        }),

      openAllSectionRoutes: () =>
        set((state) => {
          const context = state.currentContext;
          const items = getSidebarItemsByContext(context);
          return {
            sectionRoutes: {
              ...state.sectionRoutes,
              [context]: items.map((group) => group.value),
            },
          };
        }),

      closeAllSectionRoutes: () =>
        set((state) => {
          const context = state.currentContext;
          return {
            sectionRoutes: {
              ...state.sectionRoutes,
              [context]: [],
            },
          };
        }),

      // Fixed getters - don't auto-initialize when empty
      getCurrentPinnedRoutes: () => {
        const state = get();
        const context = state.currentContext;
        return state.pinnedRoutes[context] || [];
      },

      getCurrentSectionRoutes: () => {
        const state = get();
        const context = state.currentContext;
        return state.sectionRoutes[context] || [];
      },

      // Separate initialization method
      initializeRoutesForContext: (context: SidebarContext) => {
        set((state) => {
          const newPinnedRoutes = { ...state.pinnedRoutes };
          const newSectionRoutes = { ...state.sectionRoutes };

          // Only initialize if completely empty (not just length 0)
          if (!newPinnedRoutes[context]) {
            newPinnedRoutes[context] = getInitialPinnedRoutes(context);
          }

          if (!newSectionRoutes[context]) {
            newSectionRoutes[context] = getInitialSectionRoutes(context);
          }

          return {
            pinnedRoutes: newPinnedRoutes,
            sectionRoutes: newSectionRoutes,
          };
        });
      },

      // Handle sidebar collapse logic
      handleSidebarCollapse: (isCollapsed: boolean) => {
        const state = get();
        const context = state.currentContext;
        const currentRoutes = state.sectionRoutes[context] || [];

        // Only open all groups if sidebar is collapsed AND all groups are currently closed
        if (isCollapsed && currentRoutes.length === 0) {
          get().openAllSectionRoutes();
        }
      },
    }),
    {
      name: "app-storage",
      partialize: (state) => ({
        language: state.language,
        languageManuallySelected: state.languageManuallySelected,
        selectedTeam: state.selectedTeam,
        currentContext: state.currentContext,
        pinnedRoutes: state.pinnedRoutes,
        sectionRoutes: state.sectionRoutes,
      }),
    }
  )
);
